import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ErrorIcon from '@mui/icons-material/Error';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const scheduleP100 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Schedule P (100) Instructions 2025: AMT & Credit Limits"
          description="Find clear, line-by-line 2025 instructions for Schedule P (100), used with Form 100 or Form 109, including AMT and credit rules for exempt organizations. "
          keywords=""
          canonical="resources/tax-exempt/schedule-p-100/"
          ogType="article"
          jsonLdPricing="179.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Schedule P (100)</span>
          </span>{' '}
          Instructions: Alternative Minimum
          <br />
          Tax and Credit Limitations – Corporation
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 1,
          }}
        >
          Understand Schedule P (100), what each line and column requires, and
          how California AMT
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          and credit limitations are calculated.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=schedule-p-100`}
          >
            E-File Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/schedule-p-100/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-09-21T09:00:00-05:00"
                      >
                        September 21, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-09-21T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Schedule P (100) at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      {' '}
                      <strong style={{ fontWeight: 600 }}>
                        What is Schedule P (100)?
                      </strong>{' '}
                      A California schedule used to calculate{' '}
                      <strong style={{ fontWeight: 600 }}>
                        Alternative Minimum Tax (AMT)
                      </strong>{' '}
                      and certain{' '}
                      <strong style={{ fontWeight: 600 }}>
                        tax credit limitations
                      </strong>
                      .{' '}
                    </li>

                    <li>
                      {' '}
                      <strong style={{ fontWeight: 600 }}>
                        Who must file Schedule P (100)?
                      </strong>{' '}
                      Corporations, including{' '}
                      <strong style={{ fontWeight: 600 }}>
                        exempt organizations other than exempt trusts
                      </strong>
                      , may need to file based on applicable AMT adjustments,
                      preference items, loss denials, other items specified
                      under{' '}
                      <strong style={{ fontWeight: 600 }}>
                        IRC Section 59
                      </strong>
                      , and applicable California income.
                    </li>

                    <li>
                      {' '}
                      <strong style={{ fontWeight: 600 }}>
                        When must an exempt organization file?
                      </strong>{' '}
                      Generally, when applicable, AMT adjustments, preference
                      items, loss denials, and{' '}
                      <strong style={{ fontWeight: 600 }}>
                        state net unrelated business taxable income (UBTI)
                        exceed $40,000
                      </strong>{' '}
                      or when certain credit limitations apply.
                    </li>

                    <li>
                      {' '}
                      <strong style={{ fontWeight: 600 }}>
                        When must a corporation file Schedule P (100)?
                      </strong>{' '}
                      Generally, when applicable, AMT adjustments, preference
                      items, loss denials, and{' '}
                      <strong style={{ fontWeight: 600 }}>
                        state net income exceed $40,000
                      </strong>{' '}
                      or when certain credit limitations apply.
                    </li>
                    <li>
                      {' '}
                      <strong style={{ fontWeight: 600 }}>
                        Which California returns use Schedule P (100)?
                      </strong>{' '}
                      It is used with{' '}
                      <strong style={{ fontWeight: 600 }}>CA Form 100</strong>{' '}
                      and{' '}
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        <Link
                          href="/tax-exempt/efile-ftb-ca-form109-online/"
                          sx={{ color: '#2020ff' }}
                        >
                          CA Form 109
                        </Link>{' '}
                      </strong>{' '}
                      to calculate applicable AMT and credit limitations.
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What’s New for Schedule P (100) in 2025?
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    <strong style={{ fontWeight: 600 }}>
                      Program 4.0 Credit:
                    </strong>{' '}
                    A new California Motion Picture and Television Production
                    Credit applies to taxable years beginning on or after{' '}
                    <strong style={{ fontWeight: 600 }}>January 1, 2025</strong>
                    .
                  </li>
                  <li>
                    <strong style={{ fontWeight: 600 }}>
                      $5 Million Credit Limitation:
                    </strong>{' '}
                    For taxable years beginning{' '}
                    <strong style={{ fontWeight: 600 }}>
                      January 1, 2024, through December 31, 2026
                    </strong>
                    , most California tax credits are subject to a{' '}
                    <strong style={{ fontWeight: 600 }}>
                      $5 million limit
                    </strong>{' '}
                    on the amount that can reduce tax.
                  </li>
                  <li>
                    <strong style={{ fontWeight: 600 }}>
                      Refundable Credit Election:
                    </strong>{' '}
                    Certain taxpayers affected by the credit limitation may be
                    able to make an{' '}
                    <strong style={{ fontWeight: 600 }}>
                      irrevocable election for a refundable credit
                    </strong>
                    .
                  </li>
                  <li>
                    <strong style={{ fontWeight: 600 }}>NOL Suspension:</strong>{' '}
                    California has suspended the use of{' '}
                    <strong style={{ fontWeight: 600 }}>
                      net operating loss (NOL) deductions
                    </strong>{' '}
                    for certain taxable years beginning{' '}
                    <strong style={{ fontWeight: 600 }}>
                      January 1, 2024, through December 31, 2026
                    </strong>
                    , subject to applicable exceptions.
                  </li>
                </Box>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#how" sx={{ color: '#0202ff' }}>
                          How to Complete Schedule P (100): Line-by-Line
                          Instructions
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#part1" sx={{ color: '#0202ff' }}>
                          Part I – Tentative Minimum Tax (TMT) and Alternative
                          Minimum Tax (AMT) Computation
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#part2" sx={{ color: '#0202ff' }}>
                          Part II - Credits that Reduce Tax
                        </Link>
                        <List sx={{ pb: 0 }}>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#sectiona" sx={{ color: '#0202ff' }}>
                                Section A – Credits that reduce excess regular
                                tax
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#sectiona1" sx={{ color: '#0202ff' }}>
                                Section A1 – Credits That Reduce Excess Regular
                                Tax and Have No Carryover Provisions
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#sectiona2" sx={{ color: '#0202ff' }}>
                                Section A2 – Credits That Reduce Excess Regular
                                Tax and Have Carryover Provisions
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#sectionb" sx={{ color: '#0202ff' }}>
                                Section B – Credits That May Reduce Regular Tax
                                Below TMT
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#sectionc" sx={{ color: '#0202ff' }}>
                                Section C – Credits that may reduce AMT
                              </Link>
                            </ListItemText>
                          </ListItem>
                        </List>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#part3" sx={{ color: '#0202ff' }}>
                          Part III - Credit for Prior Year AMT
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#faq" sx={{ color: '#0202ff' }}>
                          Frequently Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="how"
                >
                  How to Complete Schedule P (100): Line-by-Line Instructions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schedule-p-100-amt-and-credit-limitations.webp" />
                  <source srcSet="/assets/images/schedule-p-100-amt-and-credit-limitations.png" />
                  <Box
                    component="img"
                    src="/assets/images/schedule-p-100-amt-and-credit-limitations.png"
                    loading="lazy"
                    alt="Schedule P (100) AMT and Credit limitations, Corporation details"
                    width={760}
                    height={109}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                      mt: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Corporation Name
                </Typography>{' '}
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    corporation's legal name
                  </strong>{' '}
                  as shown on the related California tax return.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <b>For example:</b>
                  <br /> If the organization files Form 109 under the name ABC
                  Community Foundation, use that same name on Schedule P (100).
                  <br />{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Corporation name: ABC Community Foundation
                  </strong>{' '}
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  California Corporation Number
                </Typography>{' '}
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the corporation's{' '}
                  <strong style={{ fontWeight: 600 }}>
                    California corporation number
                  </strong>{' '}
                  shown on its California tax filing records.
                </Typography>
                <Alert
                  severity="info"
                  icon={<TipsAndUpdatesOutlinedIcon />}
                  sx={{ mb: 2, mt: 0, borderLeft: '3px solid #0288d1' }}
                >
                  <b>Tips :</b> Utilize the corporate number for the entity
                  rather than the federal EIN in filling out this form
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="part1"
                >
                  Part I – Tentative Minimum Tax (TMT) and Alternative Minimum
                  Tax (AMT) Computation{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schedule-p-100-part-i-line-1-and-line-2.webp" />
                  <source srcSet="/assets/images/schedule-p-100-part-i-line-1-and-line-2.png" />
                  <Box
                    component="img"
                    src="/assets/images/schedule-p-100-part-i-line-1-and-line-2.png"
                    loading="lazy"
                    alt="Schedule P (100) AMT and Credit limitations, Part I, Line 1 and Line 2"
                    width={760}
                    height={264}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                      mt: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 1 – Net income (loss) after state adjustments
                </Typography>{' '}
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount from Form 100, line 17. If filing{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    <Link
                      href="/resources/tax-exempt/form-109-instructions/"
                      sx={{ color: '#2020ff' }}
                    >
                      Form 109
                    </Link>{' '}
                  </strong>{' '}
                  instead,{' '}
                  <strong style={{ fontWeight: 600 }}>
                    enter the smaller of line 1
                  </strong>{' '}
                  or line 2. If{' '}
                  <Link
                    href="/resources/tax-exempt/form-109-instructions/#schr"
                    sx={{ color: '#2020ff' }}
                  >
                    Schedule R
                  </Link>{' '}
                  was filed, use Schedule R, line 1c instead.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 2 – Adjustments
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  The adjustments are reported separately on lines 2a through
                  2k, and line 2l combines them.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 2a – Depreciation of Tangible Property
                </Typography>{' '}
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    Enter the difference between{' '}
                    <strong style={{ fontWeight: 600 }}>
                      California depreciation and AMT-recomputed depreciation
                    </strong>{' '}
                    for qualifying tangible property placed in service after
                    1986 and before 1999.{' '}
                  </li>
                  <li>
                    The adjustment is calculated on{' '}
                    <strong style={{ fontWeight: 600 }}>
                      <Link
                        href="/resources/tax-exempt/ftb-form-3885/"
                        sx={{ color: '#2020ff' }}
                      >
                        Form FTB 3885
                      </Link>
                    </strong>
                    , and the resulting difference is entered.{' '}
                  </li>
                  <li>
                    Do not include adjustments from tax shelter farm or passive
                    activities here; those are reported on{' '}
                    <strong style={{ fontWeight: 600 }}>
                      Line 2g or Line 2h
                    </strong>
                    .{' '}
                  </li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <b>For example:</b>
                  <br /> California depreciation = $10,000
                  <br />
                  AMT-recomputed depreciation = $7,000 <br />
                  <strong style={{ fontWeight: 600 }}>
                    Line 2a adjustment = $3,000
                  </strong>{' '}
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 2b – Certified Pollution Control Facilities
                </Typography>{' '}
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    Applies to certified pollution control facilities placed in
                    service after 1986.{' '}
                  </li>
                  <li>
                    For facilities placed in service before 1999,{' '}
                    <strong style={{ fontWeight: 600 }}>
                      recompute amortization using the alternative depreciation
                      system
                    </strong>{' '}
                    (straight-line, no salvage value) instead of the regular
                    five-year method.{' '}
                  </li>
                  <li>
                    For facilities placed in service after 1998, use the
                    straight-line method directly, no separate AMT recalculation
                    is needed.{' '}
                  </li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <b>For Example: </b>
                  <br />
                  Regular-tax amortization (five-year method) = $6,000
                  <br />
                  AMT amortization (straight-line, ADS) = $4,500
                  <br />
                  <strong style={{ fontWeight: 600 }}>
                    Line 2b adjustment = $1,500
                  </strong>
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 2c – Amortization of Mining Exploration and Development
                  Costs
                </Typography>{' '}
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    Enter the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      AMT adjustment for qualifying mining exploration and
                      development costs incurred after 1987
                    </strong>
                    .{' '}
                  </li>
                  <li>
                    Refigure the deduction using the required{' '}
                    <strong style={{ fontWeight: 600 }}>
                      10-year amortization period
                    </strong>{' '}
                    and report the difference from the regular-tax
                    deduction.{' '}
                  </li>
                  <li>
                    Skip this line if the corporation elected the optional{' '}
                    <strong style={{ fontWeight: 600 }}>
                      10-year write-off under IRC Section 59(e) for all assets
                    </strong>{' '}
                    in this category.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 2d – Basis Adjustments in Determining Gain or Loss From
                  Sale or Exchange of Property
                </Typography>{' '}
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    If the corporation disposed of property, enter the
                    difference between the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      regular-tax gain or loss and the AMT-recomputed gain or
                      loss
                    </strong>
                    , after taking Lines 2a–2c into account.{' '}
                  </li>
                  <li>
                    Enter the difference as a{' '}
                    <strong style={{ fontWeight: 600 }}>negative amount</strong>{' '}
                    when the AMT-recomputed gain is lower or the AMT-recomputed
                    loss is greater.{' '}
                  </li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <b>For Example: </b>
                  Regular-tax gain = $40,000; AMT-recomputed gain = $45,000 →{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Enter $5,000 on Line 2d.
                  </strong>
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 2e – Long-Term Contracts
                </Typography>{' '}
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    Applies to contracts entered{' '}
                    <strong style={{ fontWeight: 600 }}>
                      after February 28, 1986
                    </strong>
                    . Recompute income using the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      percentage-of-completion method
                    </strong>{' '}
                    under AMT rules.{' '}
                  </li>
                  <li>
                    Enter the difference from the amount reported under regular
                    tax; enter as negative if the AMT figure is lower.{' '}
                  </li>
                  <li>
                    Use{' '}
                    <strong style={{ fontWeight: 600 }}>Form FTB 3834</strong>{' '}
                    to calculate interest under the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      look-back method
                    </strong>{' '}
                    for certain completed long-term contracts.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 2f – Installment Sales of Certain Property
                </Typography>{' '}
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    When a corporation uses the installment method for regular
                    tax purposes but must recognize the full gain in the year of
                    sale for AMT purposes, enter the current-year installment
                    income reported for regular tax as a negative amount on Line
                    2f.{' '}
                  </li>
                  <li>
                    Certain qualifying installment sales by farmers are not
                    subject to this adjustment.{' '}
                  </li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <b>For Example: </b>
                  If $20,000 of installment income was reported for regular tax,
                  enter <strong style={{ fontWeight: 600 }}>
                    {' '}
                    -$20,000{' '}
                  </strong>{' '}
                  on Line 2f.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 2g – Tax Shelter Farm Activities (Personal Service
                  Corporations Only)
                </Typography>{' '}
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    Complete Line 2g only when a{' '}
                    <strong style={{ fontWeight: 600 }}>
                      personal service corporation
                    </strong>{' '}
                    has a gain or loss from a{' '}
                    <strong style={{ fontWeight: 600 }}>
                      tax shelter farm activity that is not a passive activity
                    </strong>
                    .{' '}
                  </li>
                  <li>
                    Refigure the activity using the applicable{' '}
                    <strong style={{ fontWeight: 600 }}>
                      AMT adjustments and tax preference items
                    </strong>
                    , then enter the difference between the AMT result and the
                    regular-tax result.{' '}
                  </li>
                  <li>
                    If the tax shelter farm activity is a{' '}
                    <strong style={{ fontWeight: 600 }}>
                      passive activity
                    </strong>
                    , report it with the other passive activities on{' '}
                    <strong style={{ fontWeight: 600 }}>Line 2h</strong>.
                    Refigured losses are generally suspended and carried forward
                    until the corporation has a gain from the same activity or
                    disposes of it.{' '}
                  </li>
                </Box>
                <Alert
                  severity="info"
                  icon={<ErrorIcon />}
                  sx={{ mb: 2, mt: 0, borderLeft: '3px solid #0288d1' }}
                >
                  <strong style={{ fontWeight: 600 }}>Caution:</strong> Any AMT
                  adjustment or tax preference item included on Line 2g{' '}
                  <strong style={{ fontWeight: 600 }}>
                    must not be reported again on another line of Schedule P
                    (100).
                  </strong>
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 2h – Passive Activities
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Complete Line 2h for{' '}
                  <strong style={{ fontWeight: 600 }}>
                    closely held corporations and personal service corporations
                  </strong>{' '}
                  with passive activities. Use a second{' '}
                  <strong style={{ fontWeight: 600 }}>Form FTB 3802</strong> to
                  refigure the activity for AMT by including applicable AMT
                  adjustments, tax preference items, and prior-year AMT losses.
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    <strong style={{ fontWeight: 600 }}>
                      Regular passive activities:
                    </strong>{' '}
                    Report the AMT adjustment after refiguring the passive
                    activity gain or loss.{' '}
                  </li>
                  <li>
                    <strong style={{ fontWeight: 600 }}>
                      Passive tax shelter farm activities:
                    </strong>{' '}
                    Refigure the gain or loss under AMT rules. An AMT gain may
                    offset other passive AMT losses, while an AMT loss is
                    generally{' '}
                    <strong style={{ fontWeight: 600 }}>
                      suspended and carried forward
                    </strong>{' '}
                    until there is a gain from the same activity or the activity
                    is disposed of.{' '}
                  </li>
                  <li>
                    <strong style={{ fontWeight: 600 }}>Insolvency:</strong> If
                    the corporation is insolvent at year-end, the allowable
                    passive activity loss may be increased by the amount that
                    liabilities exceed the fair market value of assets, subject
                    to the applicable limit.{' '}
                  </li>
                </Box>
                <Alert
                  severity="info"
                  icon={<ErrorIcon />}
                  sx={{ mb: 2, mt: 0, borderLeft: '3px solid #0288d1' }}
                >
                  <strong style={{ fontWeight: 600 }}>Caution:</strong> Do not
                  report an AMT adjustment or tax preference item included on
                  Line 2h again on another Schedule P (100) line.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 2i – Certain Loss Limitations
                </Typography>{' '}
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    Refigure allowable losses from{' '}
                    <strong style={{ fontWeight: 600 }}>
                      at-risk activities and applicable partnership basis
                      limitations
                    </strong>{' '}
                    using AMT adjustments and tax preference items.{' '}
                  </li>
                  <li>
                    Enter the resulting difference on this line; when the AMT
                    loss is greater, the adjustment is entered as a{' '}
                    <strong style={{ fontWeight: 600 }}>negative amount</strong>
                    .{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 2j – Beneficiaries of Estates and Trusts
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Enter the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    amount from Schedule K-1 (541), line 12a
                  </strong>{' '}
                  that represents the applicable AMT adjustment received as a
                  beneficiary of an estate or trust.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 2k – Merchant Marine Capital Construction Funds
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Enter amounts that were{' '}
                  <strong style={{ fontWeight: 600 }}>
                    deducted or excluded for regular tax purposes
                  </strong>{' '}
                  but are not deductible or excludable for AMT purposes. This
                  includes qualifying deposits and earnings related to{' '}
                  <strong style={{ fontWeight: 600 }}>
                    merchant marine capital construction funds
                  </strong>
                  .
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 2l – Combine Lines 2a Through 2k
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Add the amounts reported on{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Lines 2a through 2k
                  </strong>{' '}
                  and enter the total on Line 2l. This gives the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    total AMT adjustments
                  </strong>{' '}
                  carried into the next stage of the Schedule P (100)
                  calculation.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schedule-p-100-part-i-line-3-to-line-6.webp" />
                  <source srcSet="/assets/images/schedule-p-100-part-i-line-3-to-line-6.png" />
                  <Box
                    component="img"
                    src="/assets/images/schedule-p-100-part-i-line-3-to-line-6.png"
                    loading="lazy"
                    alt="Schedule P (100) AMT and Credit limitations, Part I, Line 3 to Line 6"
                    width={760}
                    height={296}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                      mt: 0,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 3 – Depletion
                </Typography>{' '}
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    Report the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      adjustment for depletion
                    </strong>{' '}
                    when the depletion deduction under IRC Section 611 is
                    greater than the property's adjusted basis at the end of the
                    year.{' '}
                  </li>
                  <li>
                    Calculate the excess separately for each mine, well, or
                    other natural deposit and enter the total on{' '}
                    <strong style={{ fontWeight: 600 }}>Line 3</strong>.{' '}
                  </li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <b>For Example: </b>
                  Depletion deduction = $30,000 and adjusted basis = $20,000 →{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Enter $10,000 on Line 3.
                  </strong>
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 4 – Pre-Adjustment Alternative Minimum Taxable Income
                  (AMTI)
                </Typography>{' '}
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 4a – Combine Lines 1, 2l, and 3
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Add{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Line 1, Line 2l, and Line 3
                  </strong>{' '}
                  to determine the corporation's{' '}
                  <strong style={{ fontWeight: 600 }}>
                    pre-adjustment AMTI
                  </strong>
                  . Enter the total on Line 4a.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 4b – Apportioned Pre-Adjustment AMTI{' '}
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    If the corporation has income from{' '}
                    <strong style={{ fontWeight: 600 }}>
                      within and outside California
                    </strong>
                    , apportion the pre-adjustment AMTI to California using the
                    applicable California apportionment rules.
                  </li>
                  <li>
                    Refigure{' '}
                    <strong style={{ fontWeight: 600 }}> Schedule R </strong>{' '}
                    using the AMT adjustments and enter the resulting amount
                    from{' '}
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Schedule R, line 35{' '}
                    </strong>{' '}
                    on Line 4b. Otherwise, enter the amount from{' '}
                    <strong style={{ fontWeight: 600 }}>Line 4a</strong>.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 5 – Adjusted Current Earnings (ACE) Adjustment
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  The ACE adjustment modifies AMTI based on{' '}
                  <strong style={{ fontWeight: 600 }}>
                    adjusted current earnings (ACE)
                  </strong>
                  . The calculation uses the ACE worksheet provided with the FTB
                  instructions.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 5a – ACE (Adjusted Current Earnings)
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  Calculate{' '}
                  <strong style={{ fontWeight: 600 }}>
                    California Adjusted Current Earnings (ACE)
                  </strong>{' '}
                  using the{' '}
                  <strong style={{ fontWeight: 600 }}>ACE worksheet</strong>{' '}
                  included with the Schedule P (100) instructions. California
                  generally follows the federal ACE rules under IRC Section
                  56(g), with California-specific modifications. Start with the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    pre-adjustment AMTI from Line 4a
                  </strong>{' '}
                  and make the required ACE adjustments.
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    <strong style={{ fontWeight: 600 }}>Taxes:</strong>{' '}
                    Income-based taxes are generally not deductible from
                    earnings and profits (E&amp;P), including qualifying foreign
                    income taxes even when no federal foreign tax credit is
                    claimed.{' '}
                  </li>
                  <li>
                    <strong style={{ fontWeight: 600 }}>
                      Depreciation and amortization:
                    </strong>{' '}
                    The ACE depreciation rules depend on{' '}
                    <strong style={{ fontWeight: 600 }}>
                      when the property was placed in service
                    </strong>
                    . For property placed in service on or after January 1,
                    1998, ACE depreciation generally follows AMT depreciation,
                    so no separate ACE depreciation adjustment is required.{' '}
                  </li>
                  <li>
                    <strong style={{ fontWeight: 600 }}>Dividends:</strong>{' '}
                    Dividends deductible for regular California tax purposes are
                    generally deductible from E&amp;P, but certain federal
                    dividend provisions do not apply for California ACE
                    purposes.{' '}
                  </li>
                  <li>
                    <strong style={{ fontWeight: 600 }}>
                      Interest income and expense:
                    </strong>{' '}
                    For entities not subject to the minimum franchise tax,
                    interest income included in E&amp;P cannot exceed the
                    interest income reported for regular tax purposes. Interest
                    expense deductions must also follow the applicable
                    California limitations.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 5b – Apportioned ACE
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    For an{' '}
                    <strong style={{ fontWeight: 600 }}>
                      apportioning taxpayer or a corporation included in a
                      combined report
                    </strong>
                    , determine the California portion of{' '}
                    <strong style={{ fontWeight: 600 }}>
                      Adjusted Current Earnings (ACE)
                    </strong>{' '}
                    using the same apportionment method used for{' '}
                    <strong style={{ fontWeight: 600 }}>
                      net income for regular tax and AMTI
                    </strong>
                    .{' '}
                  </li>
                  <li>
                    The method described for{' '}
                    <strong style={{ fontWeight: 600 }}>Line 4b</strong> may
                    also be used to calculate California ACE.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 5c – Difference Between Apportioned ACE and
                  Pre-Adjustment AMTI
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  Subtract{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Line 4b from Line 5b
                  </strong>
                  . Complete the calculation even when either amount is
                  negative. Use brackets when the result is negative.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <b>For Example: </b>
                  Line 5b = $120,000 and Line 4b = $100,000 →{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Line 5c = $20,000.
                  </strong>
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 5d – 75% of the Line 5c Amount
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  Multiply{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Line 5c by 75% (0.75)
                  </strong>{' '}
                  and enter the result as a positive amount.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <b>For Example: </b>
                  Line 5c = $20,000 →{' '}
                  <strong style={{ fontWeight: 600 }}>Line 5d = $15,000</strong>
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 5e – Prior-Year ACE Adjustments
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  For a{' '}
                  <strong style={{ fontWeight: 600 }}>combined report</strong>,
                  each corporation must enter the excess of its accumulated
                  positive California ACE adjustments from prior years over its
                  accumulated negative California ACE adjustments.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 5f – ACE Adjustment
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  Use the amount from{' '}
                  <strong style={{ fontWeight: 600 }}>Line 5c</strong> to
                  determine the ACE adjustment:
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    When{' '}
                    <strong style={{ fontWeight: 600 }}>
                      Line 5c is zero or greater
                    </strong>
                    , report the amount from{' '}
                    <strong style={{ fontWeight: 600 }}>
                      Line 5d as a positive adjustment
                    </strong>
                    .{' '}
                  </li>
                  <li>
                    When{' '}
                    <strong style={{ fontWeight: 600 }}>
                      Line 5c is negative
                    </strong>
                    , compare{' '}
                    <strong style={{ fontWeight: 600 }}>Lines 5d and 5e</strong>{' '}
                    and enter the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      smaller amount as a negative adjustment
                    </strong>
                    .{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 6 – Combine Line 4b and Line 5f
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Add{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Line 4b and Line 5f
                  </strong>
                  . If the result is zero or less, enter{' '}
                  <strong style={{ fontWeight: 600 }}>0</strong>.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schedule-p-100-part-i-line-7-to-line-16.webp" />
                  <source srcSet="/assets/images/schedule-p-100-part-i-line-7-to-line-16.png" />
                  <Box
                    component="img"
                    src="/assets/images/schedule-p-100-part-i-line-7-to-line-16.png"
                    loading="lazy"
                    alt="Schedule P (100) AMT and Credit limitations, Part I, Line 7 to Line 16"
                    width={760}
                    height={201}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                      mt: 0,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 7a – Reduction for Disaster Loss Deduction
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    Enter the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      2025 disaster loss deduction
                    </strong>{' '}
                    claimed by the corporation. This deduction is{' '}
                    <strong style={{ fontWeight: 600 }}>
                      not subject to California's NOL suspension
                    </strong>{' '}
                    for taxable years beginning on or after January 1, 2024, and
                    before January 1, 2027.{' '}
                  </li>
                  <li>
                    Any remaining 2025 qualified disaster loss is carried
                    forward under the applicable NOL rules.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 7b – AMT Net Operating Loss Deduction
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  Enter the corporation's{' '}
                  <strong style={{ fontWeight: 600 }}>
                    allowable AMT net operating loss (NOL) deduction
                  </strong>
                  . The AMT NOL is generally based on the regular-tax NOL, with
                  required AMT adjustments, tax preference items, and expired
                  losses taken into account.
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    The AMT NOL deduction cannot exceed{' '}
                    <strong style={{ fontWeight: 600 }}>
                      90% of the amount on Line 6
                    </strong>
                    .{' '}
                  </li>
                  <li>
                    Enter the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      smaller of the allowable AMT NOL or 90% of Line 6
                    </strong>
                    .{' '}
                  </li>
                  <li>
                    For taxable years beginning in 2024 through 2026,
                    California's NOL suspension generally applies, subject to
                    exceptions for corporations with{' '}
                    <strong style={{ fontWeight: 600 }}>
                      taxable income under $1 million
                    </strong>{' '}
                    and corporations with{' '}
                    <strong style={{ fontWeight: 600 }}>
                      disaster loss carryovers
                    </strong>
                    .{' '}
                  </li>
                  <li>
                    Members of a{' '}
                    <strong style={{ fontWeight: 600 }}>
                      unitary group filing a combined report
                    </strong>{' '}
                    must calculate and apply the NOL separately for each
                    corporation.{' '}
                  </li>
                  <li>
                    The AMT NOL carryover may differ from the regular-tax NOL
                    carryover, so separate records should be maintained.{' '}
                  </li>
                  <li>
                    <strong style={{ fontWeight: 600 }}>Form FTB 3805Q</strong>{' '}
                    provides the applicable NOL and disaster loss
                    calculations.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 7c – Combine Lines 7a and 7b
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  Add{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Line 7a and Line 7b
                  </strong>{' '}
                  and enter the combined amount on Line 7c. This total is
                  subtracted from Line 6 to determine{' '}
                  <strong style={{ fontWeight: 600 }}>AMTI on Line 8</strong>.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <b>For Example: </b>
                  Line 7a = $5,000 and Line 7b = $10,000 →{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Line 7c = $15,000.
                  </strong>
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 8 – Alternative Minimum Taxable Income (AMTI)
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Subtract{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Line 7c from Line 6
                  </strong>{' '}
                  to calculate the corporation's{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Alternative Minimum Taxable Income (AMTI)
                  </strong>
                  .
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 9 – $40,000 Exemption
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Enter the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    $40,000 AMT exemption
                  </strong>{' '}
                  shown on the schedule. For corporations included in a combined
                  report, the exemption applies separately to each corporation
                  that has a California filing requirement and AMTI.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 10 – $150,000 Limitation
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Enter the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    $150,000 limitation
                  </strong>{' '}
                  used with the AMT exemption calculation. The $40,000 exemption
                  and $150,000 limitation apply to each qualifying corporation
                  in a combined report to the extent the corporation has AMTI.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 11 – AMTI Subject to Exemption Phaseout
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Subtract{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Line 10 from Line 8
                  </strong>
                  . If the result is zero or less, enter{' '}
                  <strong style={{ fontWeight: 600 }}>0</strong>.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 12 – 25% of Line 11
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Multiply{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Line 11 by 25% (0.25)
                  </strong>{' '}
                  and enter the result. This amount is used to reduce the
                  $40,000 exemption on Line 13.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 13 – Reduced AMT Exemption
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Subtract{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Line 12 from Line 9
                  </strong>
                  . If the result is zero or less, enter{' '}
                  <strong style={{ fontWeight: 600 }}>0</strong>. This gives the
                  corporation's{' '}
                  <strong style={{ fontWeight: 600 }}>
                    allowable AMT exemption
                  </strong>
                  .
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 14 – AMT Taxable Amount
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Subtract{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Line 13 from Line 8
                  </strong>
                  . If the result is zero or less, enter{' '}
                  <strong style={{ fontWeight: 600 }}>0</strong>. This is the
                  amount subject to the California AMT rate on Line 15.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 15 – Tentative Minimum Tax
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Multiply{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Line 14 by 6.65% (0.0665)
                  </strong>{' '}
                  to calculate the corporation's{' '}
                  <strong style={{ fontWeight: 600 }}>
                    California tentative minimum tax (TMT)
                  </strong>
                  .
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 16 – Banks and Financial Corporations
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  This line applies specifically to{' '}
                  <strong style={{ fontWeight: 600 }}>
                    banks and financial corporations
                  </strong>
                  . Enter{' '}
                  <strong style={{ fontWeight: 600 }}>
                    2.00% of Form 100, line 22
                  </strong>
                  . If Form 100, line 22 is zero or negative, enter{' '}
                  <strong style={{ fontWeight: 600 }}>0</strong>.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schedule-p-100-part-i-line-17-to-line-19.webp" />
                  <source srcSet="/assets/images/schedule-p-100-part-i-line-17-to-line-19.png" />
                  <Box
                    component="img"
                    src="/assets/images/schedule-p-100-part-i-line-17-to-line-19.png"
                    loading="lazy"
                    alt="Schedule P (100) AMT and Credit limitations, Part I, Line 17 to Line 19"
                    width={760}
                    height={84}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                      mt: 0,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 17 – Tentative Minimum Tax (TMT)
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Add{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Line 15 and Line 16
                  </strong>{' '}
                  to calculate the corporation's{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Tentative Minimum Tax (TMT)
                  </strong>
                  . Enter the total on Line 17.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 18 – Regular Tax Before Credits
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Enter the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    regular California tax before credits
                  </strong>{' '}
                  from:
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    <strong style={{ fontWeight: 600 }}>
                      Form 100, Line 23
                    </strong>
                    , or{' '}
                  </li>
                  <li>
                    <strong style={{ fontWeight: 600 }}>
                      Form 109, Line 10.
                    </strong>{' '}
                  </li>
                </Box>
                <Typography sx={{ mb: 2 }}>
                  For certain installment obligations, do not include tax
                  increases attributable to interest on deferred tax liability
                  subjected to IRC Section 453(l)(2)(B) and IRC Section 453A.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 19 – Alternative Minimum Tax (AMT)
                </Typography>{' '}
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    Subtract{' '}
                    <strong style={{ fontWeight: 600 }}>
                      Line 18 from Line 17
                    </strong>{' '}
                    to determine the corporation's{' '}
                    <strong style={{ fontWeight: 600 }}>California AMT</strong>.
                    If the result is zero or less, enter{' '}
                    <strong style={{ fontWeight: 600 }}>0</strong>.{' '}
                  </li>
                  <li>
                    If Line 17 is greater than zero and the corporation has
                    credits or credit carryovers, continue to{' '}
                    <strong style={{ fontWeight: 600 }}>Part II</strong>.
                    Otherwise, report the Line 19 amount on{' '}
                    <strong style={{ fontWeight: 600 }}>
                      Form 100, Line 29, or Form 109, Line 13
                    </strong>
                    .{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="part2"
                >
                  Part II - Credits that Reduce Tax
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Part II is completed{' '}
                  <strong style={{ fontWeight: 600 }}>
                    only when the corporation has tax credits
                  </strong>
                  . It determines how much of those credits can be used against
                  California tax, the amount of tax available to offset, and any
                  credit that can be carried forward.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schedule-p-100-part-ii.webp" />
                  <source srcSet="/assets/images/schedule-p-100-part-ii.png" />
                  <Box
                    component="img"
                    src="/assets/images/schedule-p-100-part-ii.png"
                    loading="lazy"
                    alt="Schedule P (100) AMT and Credit limitations, Part II, Line 1 and Line 2"
                    width={760}
                    height={50}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                      mt: 0,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 1 – Regular Tax
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Enter the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    regular California tax before credits
                  </strong>{' '}
                  from{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Form 100, line 23, or Form 109, line 10
                  </strong>
                  . This is the starting tax amount against which allowable
                  credits are applied in Part II.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 2 – Tentative Minimum Tax (TMT)
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Enter the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    TMT from Part I, line 17
                  </strong>
                  , but use at least the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    minimum franchise tax
                  </strong>
                  , when applicable. This amount establishes the minimum tax
                  level that certain credits cannot reduce regular tax below.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Column Instructions
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  The columns in Sections A–C are used to determine the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    credit available, the amount that can be used in the current
                    year, the remaining tax available for credits, and the
                    credit carryover
                  </strong>
                  .
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Column (a) – Credit Amount
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Enter the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    amount of credit available to offset the tax
                  </strong>{' '}
                  for the current year.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Column (b) – Credit Used This Year
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Enter the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    smaller of the credit in Column (a) or the tax remaining in
                    Column (c) from the previous line
                  </strong>
                  .
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    Total credits claimed in Column (b) generally cannot exceed{' '}
                    <strong style={{ fontWeight: 600 }}>$5 million</strong>
                    .{' '}
                  </li>
                  <li>
                    For a{' '}
                    <strong style={{ fontWeight: 600 }}>combined report</strong>
                    , the $5 million limit applies at the{' '}
                    <strong style={{ fontWeight: 600 }}>group level</strong>
                    .{' '}
                  </li>
                  <li>
                    The limitation does{' '}
                    <strong style={{ fontWeight: 600 }}>not</strong> apply to
                    the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      Low-Income Housing Credit
                    </strong>{' '}
                    or the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      prior-year AMT credit
                    </strong>
                    .{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Column (c) – Tax Remaining
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Subtract the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    credit used in Column (b)
                  </strong>{' '}
                  from the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    tax balance in Column (c) of the previous line
                  </strong>
                  . The result is the amount of tax that remains available to be
                  offset by other credits.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Column (d) – Credit Carryover
                </Typography>{' '}
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    Subtract{' '}
                    <strong style={{ fontWeight: 600 }}>
                      Column (b) from Column (a)
                    </strong>{' '}
                    to determine the credit that remains available for future
                    years.{' '}
                  </li>
                  <li>
                    Keep separate records of credit amounts that could not be
                    used because of the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      $5 million credit limitation
                    </strong>
                    , as these amounts may be available in later years.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="sectiona"
                >
                  Section A – Credits that reduce excess regular tax
                </Typography>{' '}
                <picture>
                  <source srcSet="/assets/images/schedule-p-100-section-a.webp" />
                  <source srcSet="/assets/images/schedule-p-100-section-a.png" />
                  <Box
                    component="img"
                    src="/assets/images/schedule-p-100-section-a.png"
                    loading="lazy"
                    alt="Schedule P (100) AMT and Credit limitations, Part II, Section A, Line 3 to Line 9"
                    width={760}
                    height={231}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                      mt: 1.5,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 3 – Excess Regular Tax
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Subtract{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Part II, Line 2 from Part II, Line 1
                  </strong>
                  .
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    <strong style={{ fontWeight: 600 }}>
                      If the result is greater than zero:
                    </strong>{' '}
                    Continue to the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      Section A1 instructions
                    </strong>
                    .{' '}
                  </li>
                  <li>
                    <strong style={{ fontWeight: 600 }}>
                      If the result is zero or less:
                    </strong>{' '}
                    Follow{' '}
                    <strong style={{ fontWeight: 600 }}>
                      Questions 1 and 2
                    </strong>{' '}
                    to determine whether the credit should be claimed in{' '}
                    <strong style={{ fontWeight: 600 }}>
                      Section B or Section C
                    </strong>
                    , carried forward to a future year, or not claimed.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 83,
                  }}
                  id="sectiona1"
                >
                  Section A1 – Credits That Reduce Excess Regular Tax and Have
                  No Carryover Provisions
                </Typography>{' '}
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 4 – Prison Inmate Labor Credit
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Complete{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Line 4, Columns (a) through (c)
                  </strong>{' '}
                  if the corporation has the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Prison Inmate Labor Credit
                  </strong>{' '}
                  listed in this section.
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    For taxable years beginning on or after{' '}
                    <strong style={{ fontWeight: 600 }}>
                      January 1, 2024, and before January 1, 2027
                    </strong>
                    , any portion of this credit disallowed because of the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      $5 million credit limitation
                    </strong>{' '}
                    may be carried forward.{' '}
                  </li>
                  <li>
                    The carryover period is extended by the number of taxable
                    years the credit was disallowed.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="sectiona2"
                >
                  Section A2 – Credits That Reduce Excess Regular Tax and Have
                  Carryover Provisions
                </Typography>{' '}
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    Section A2 covers{' '}
                    <strong style={{ fontWeight: 600 }}>
                      credits with carryover provisions
                    </strong>{' '}
                    that may be used to reduce the corporation's excess regular
                    tax. The FTB Credit Table should be used to identify which
                    credits belong in Section A2 and their applicable credit
                    codes.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Lines 5 – Line 8 – Credits With Carryover Provisions
                </Typography>{' '}
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    Use{' '}
                    <strong style={{ fontWeight: 600 }}>
                      Lines 5 through 8
                    </strong>{' '}
                    to report the credits listed in{' '}
                    <strong style={{ fontWeight: 600 }}>Section A2</strong> that
                    can reduce excess regular tax and have{' '}
                    <strong style={{ fontWeight: 600 }}>
                      carryover provisions
                    </strong>
                    . For each applicable credit, enter the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      credit code, credit name, and available credit
                    </strong>
                    , then complete{' '}
                    <strong style={{ fontWeight: 600 }}>
                      Columns (a) through (d)
                    </strong>{' '}
                    according to the Part II column instructions.{' '}
                  </li>
                  <li>
                    Apply the credits in the order required by the FTB
                    instructions, including using the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      prior-year AMT credit before other applicable credits
                    </strong>
                    . Unused amounts may generally be carried forward according
                    to the specific credit rules.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 9 – Prior-Year AMT Credit
                </Typography>{' '}
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    Enter the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      credit for prior year AMT from Part III, Line 3
                    </strong>
                    . This credit is identified as{' '}
                    <strong style={{ fontWeight: 600 }}>Credit Code 188</strong>{' '}
                    on the 2025 Schedule P (100) and must be applied{' '}
                    <strong style={{ fontWeight: 600 }}>
                      before other credits that can reduce regular tax below TMT
                    </strong>
                    .{' '}
                  </li>
                  <li>
                    Credits with{' '}
                    <strong style={{ fontWeight: 600 }}>
                      limited carryover periods are generally applied before
                      credits with unlimited carryover periods
                    </strong>
                    . However, the corporation may use a different order when it
                    is more beneficial.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="sectionb"
                >
                  Section B – Credits That May Reduce Regular Tax Below TMT
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Section B covers{' '}
                  <strong style={{ fontWeight: 600 }}>
                    tax credits that can reduce a corporation's regular
                    California tax below its Tentative Minimum Tax (TMT)
                  </strong>
                  . These credits may also be carried forward when allowed under
                  the applicable credit rules.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schedule-p-100-section-b.webp" />
                  <source srcSet="/assets/images/schedule-p-100-section-b.png" />
                  <Box
                    component="img"
                    src="/assets/images/schedule-p-100-section-b.png"
                    loading="lazy"
                    alt="Schedule P (100) AMT and Credit limitations, Part II, Section B, Line 10 to Line 14"
                    width={760}
                    height={182}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                      mt: 0,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 10 – Tax Available for Section B Credits
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Calculate the amount of{' '}
                  <strong style={{ fontWeight: 600 }}>
                    regular tax available to be reduced by Section B credits
                  </strong>
                  :
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    When{' '}
                    <strong style={{ fontWeight: 600 }}>
                      Part II, Line 3 is zero
                    </strong>
                    , subtract the applicable{' '}
                    <strong style={{ fontWeight: 600 }}>
                      minimum franchise tax
                    </strong>{' '}
                    from Part II, Line 1.{' '}
                  </li>
                  <li>
                    When{' '}
                    <strong style={{ fontWeight: 600 }}>
                      Line 3 is greater than zero
                    </strong>
                    , use the applicable amount of{' '}
                    <strong style={{ fontWeight: 600 }}>Part II, Line 2</strong>
                    , adjusted for the minimum franchise tax, and add the amount
                    from{' '}
                    <strong style={{ fontWeight: 600 }}>
                      Line 9, Column (c)
                    </strong>{' '}
                    or the last applicable amount in Column (c).{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Lines 11–14 – Credits That Reduce Net Tax and have carryover
                  provisions
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Use{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Lines 11 through 14
                  </strong>{' '}
                  to report credits listed by the FTB as{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Section B credits with carryover provisions
                  </strong>
                  .
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  For each applicable credit:
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    Enter the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      credit code and credit name
                    </strong>
                    .{' '}
                  </li>
                  <li>
                    Complete{' '}
                    <strong style={{ fontWeight: 600 }}>
                      Columns (a) through (d)
                    </strong>{' '}
                    using the Part II column instructions.{' '}
                  </li>
                  <li>
                    Apply the credit against the tax amount determined through{' '}
                    <strong style={{ fontWeight: 600 }}>Line 10</strong>.{' '}
                  </li>
                  <li>
                    Any unused amount may generally be{' '}
                    <strong style={{ fontWeight: 600 }}>carried forward</strong>
                    , subject to the specific credit's rules.{' '}
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{
                    mb: 4,
                    mt: 4,
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> For an
                  organization claiming{' '}
                  <strong style={{ fontWeight: 600 }}>
                    one to three credits
                  </strong>
                  , enter each credit's{' '}
                  <strong style={{ fontWeight: 600 }}>
                    name, code, and Column (b) amount on Form 109, Schedule B,
                    Lines 1–3
                  </strong>
                  . Organizations claiming{' '}
                  <strong style={{ fontWeight: 600 }}>
                    more than three credits
                  </strong>{' '}
                  should follow the{' '}
                  <Link
                    href="/resources/tax-exempt/form-109-instructions/#schb"
                    sx={{ color: '#2020ff' }}
                  >
                    Form 109 Schedule B instructions
                  </Link>
                  .
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="sectionc"
                >
                  Section C – Credits that may reduce AMT
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Section C is used when a corporation has{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Alternative Minimum Tax (AMT)
                  </strong>{' '}
                  remaining after reducing regular tax to the applicable minimum
                  franchise tax. Certain{' '}
                  <strong style={{ fontWeight: 600 }}>credit carryovers</strong>{' '}
                  may then be used to reduce the AMT.
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    Eligible credits include{' '}
                    <strong style={{ fontWeight: 600 }}>
                      Solar Energy Credit, Commercial Solar Energy Credit, and
                      Enterprise Zone Hiring &amp; Sales or Use Tax Credit
                    </strong>{' '}
                    carryovers.{' '}
                  </li>
                  <li>
                    Any eligible credit remaining after reducing AMT to{' '}
                    <strong style={{ fontWeight: 600 }}>zero</strong> may
                    generally be{' '}
                    <strong style={{ fontWeight: 600 }}>
                      carried forward to future taxable years
                    </strong>
                    .{' '}
                  </li>
                  <li>
                    The{' '}
                    <strong style={{ fontWeight: 600 }}>
                      Manufacturer's Investment Credit (MIC)
                    </strong>{' '}
                    can no longer be used to offset AMT because its carryover
                    period has expired.{' '}
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/schedule-p-100-section-c.webp" />
                  <source srcSet="/assets/images/schedule-p-100-section-c.png" />
                  <Box
                    component="img"
                    src="/assets/images/schedule-p-100-section-c.png"
                    loading="lazy"
                    alt="Schedule P (100) AMT and Credit limitations, Part II, Section C, Line 15 to Line 18"
                    width={760}
                    height={150}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                      mt: 0,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 15 – AMT
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Enter the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    AMT from Part I, Line 19
                  </strong>
                  . This is the amount of California AMT that may be reduced by
                  the eligible credits in Section C.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Lines 16a – Solar Energy Credit Carryovers
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Enter{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Credit Code 180 – Solar Energy Credit
                  </strong>{' '}
                  carryover from{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Section B, Column (d)
                  </strong>
                  .
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 16b – Commercial Solar Energy Credit Carryovers
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Enter{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Credit Code 181 – Commercial Solar Energy Credit
                  </strong>{' '}
                  carryover from{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Section B, Column (d)
                  </strong>
                  . These carryovers may be used in Section C to reduce the AMT
                  shown on Line 15.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 17 – Enterprise Zone Hiring &amp; Sales or Use Tax Credit
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Enter{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Credit Code 176 – Enterprise Zone Hiring &amp; Sales or Use
                    Tax Credit
                  </strong>{' '}
                  carryover from{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Section B, Column (d)
                  </strong>
                  .
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 18 – Adjusted AMT
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Enter the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    remaining balance from Line 17, Column (c)
                  </strong>
                  . This amount is the corporation's{' '}
                  <strong style={{ fontWeight: 600 }}>adjusted AMT</strong>{' '}
                  after applying the allowable Section C credits.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="part3"
                >
                  Part III – Credit for Prior Year AMT
                </Typography>{' '}
                <picture>
                  <source srcSet="/assets/images/schedule-p-100-part-iii.webp" />
                  <source srcSet="/assets/images/schedule-p-100-part-iii.png" />
                  <Box
                    component="img"
                    src="/assets/images/schedule-p-100-part-iii.png"
                    loading="lazy"
                    alt="Schedule P (100) AMT and Credit limitations, Part III, Line 1 to Line 3"
                    width={760}
                    height={69}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                      mt: 1.5,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 1 – AMT From Prior Year
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Enter the corporation's AMT from the 2024{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Schedule P (100), Part I, line 19.
                  </strong>
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 2 – Prior-Year AMT Credit Carryover
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Enter the unused{' '}
                  <strong style={{ fontWeight: 600 }}>
                    prior-year AMT credit carryover
                  </strong>{' '}
                  from{' '}
                  <strong style={{ fontWeight: 600 }}>
                    2024 Schedule P (100), Part II, Line 9, Column (d)
                  </strong>
                  . This is the portion of the prior-year AMT credit that
                  remains available for use in 2025.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 3 – Total Available Prior-Year AMT Credit
                </Typography>{' '}
                <Typography sx={{ mb: 2 }}>
                  Add{' '}
                  <strong style={{ fontWeight: 600 }}>Line 1 and Line 2</strong>
                  . Enter the total on{' '}
                  <strong style={{ fontWeight: 600 }}>Part III, Line 3</strong>,
                  and then carry the amount to{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Part II, Line 9, Column (a)
                  </strong>{' '}
                  as the corporation's available prior-year AMT credit.
                </Typography>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        This guide is based on the official California Franchise
                        Tax Board (FTB){' '}
                        <strong style={{ fontWeight: 600 }}>
                          2025 Schedule P (100) form and{' '}
                          <Link
                            href="https://www.ftb.ca.gov/forms/2025/2025-100-p-instructions.html"
                            sx={{ color: '#2020ff' }}
                            target="_blank"
                          >
                            instructions
                          </Link>
                        </strong>
                        .{' '}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
                scrollMarginTop: 83,
              }}
              id="faq"
            >
              Frequently Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. We are an exempt trust with unrelated business income.
                  Should we file Schedule P (100) or Schedule P (541)?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography variant="body1" itemProp="text" sx={{ mt: 1 }}>
                    An{' '}
                    <strong style={{ fontWeight: 600 }}>
                      exempt trust with unrelated business income generally uses
                      Schedule P (541), not Schedule P (100)
                    </strong>
                    . The California FTB treats{' '}
                    <strong style={{ fontWeight: 600 }}>
                      exempt organizations other than exempt trusts
                    </strong>{' '}
                    as corporations for Schedule P (100) purposes, while{' '}
                    <strong style={{ fontWeight: 600 }}>
                      exempt trusts are covered under Schedule P (541)
                    </strong>
                    .{' '}
                  </Typography>
                  <Typography variant="body1" itemProp="text" sx={{ mt: 1 }}>
                    For organizations filing{' '}
                    <strong style={{ fontWeight: 600 }}>Form 109</strong>,{' '}
                    Schedule P (100) may apply when the organization meets the
                    applicable AMT or credit requirements.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. Our exempt organization is part of a combined report. Can
                  we complete one Schedule P (100) for the whole group?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography variant="body1" itemProp="text" sx={{ mt: 1 }}>
                    No. The FTB requires a{' '}
                    <strong style={{ fontWeight: 600 }}>
                      separate Schedule P (100) for each corporation included in
                      a combined report
                    </strong>
                    , with AMTI and ACE separately apportioned and allocated to
                    each member. The $5 million credit limitation is applied at
                    the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      combined-group level
                    </strong>
                    .{' '}
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. I’m filing Form 109 for an exempt organization. When would
                  Schedule P (100) also be required?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography variant="body1" itemProp="text" sx={{ mt: 1 }}>
                    An exempt organization{' '}
                    <Link
                      href="/tax-exempt/efile-ftb-ca-form109-online/"
                      sx={{ color: '#2020ff' }}
                    >
                      filing Form 109
                    </Link>{' '}
                    generally completes Schedule P (100) when its applicable{' '}
                    <strong style={{ fontWeight: 600 }}>
                      AMT items and state net
                    </strong>{' '}
                    unrelated business taxable income{' '}
                    <strong style={{ fontWeight: 600 }}>exceed $40,000</strong>,{' '}
                    or when it claims credits subject to{' '}
                    <strong style={{ fontWeight: 600 }}>
                      TMT or AMT limitations. Exempt trusts generally use
                      Schedule P (541)
                    </strong>{' '}
                    instead.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default scheduleP100;
